/**
 * SSIDs — the site's Wi-Fi networks (Wireless > SSIDs).
 *
 * Site-scoped: an SSID applies to every AP at the site (owner ruling), so create/
 * edit/delete fan out through the controller -> owgw with a per-device audit. The
 * passphrase is write-only — it is sent on save but never returned or shown.
 * Requires a site in scope; at customer scope we prompt to pick one.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import * as Dialog from "@radix-ui/react-dialog";
import { useSession } from "@/stores/session";
import {
  useSsids,
  useUpsertSsid,
  useRenameSsid,
  useDeleteSsid,
  useToggleSsid,
  useSiteTags,
  useRadiusServers,
  useValidateSsid,
  useSsidPushStatus,
} from "@/lib/api/hooks";
import { ApiError } from "@/lib/api/client";
import { PageHeader, ErrorBanner, EmptyState } from "@/components/ui/Page";
import { Button } from "@/components/ui/Button";
import { RoleGate } from "@/components/auth/RoleGate";
import { PathCompactLine, VlanPathButton } from "@/components/site/VlanPathCheck";
import { hasPathWarning, type VlanPathCompact } from "@/lib/vlanPath";
import {
  OUTCOME_LABEL,
  OUTCOME_ORDER,
  outcomeTone,
  reasonTone,
  summarizeRows,
  namesWith,
} from "@/lib/ssidPushOutcome";
import { HOST_LOCKED_HINT, REENTER_HINT, hostMoved } from "@/lib/radiusDestination";
import { apListLabel, canSaveAnyway, coverageGap, darkAps, hardWarnings as pickHard, targetLabel } from "@/lib/ssidSafety";
import type {
  Ssid,
  SsidSecurity,
  SsidScheduleWindow,
  SsidPushOutcome,
  SsidPushRow,
  SsidValidation,
  SsidWarning,
  SsidExcludedDevice,
  SsidImpact,
  SsidImpactAp,
} from "@/types/domain";

const SECURITY_LABEL: Record<SsidSecurity, string> = {
  open: "Open",
  wpa2: "WPA2",
  wpa3: "WPA3",
  "wpa2/3": "WPA2/3",
  "wpa2-enterprise": "WPA2-Enterprise",
  "wpa3-enterprise": "WPA3-Enterprise",
};
const ALL_BANDS = ["2G", "5G", "6G"] as const;

/**
 * Security modes 6 GHz refuses. Mirrors services/ssid_manager._NOT_ALLOWED_ON_6G -- the backend
 * is authoritative and refuses the save too; this only stops the operator earlier.
 */
const NOT_ALLOWED_ON_6G: Partial<Record<SsidSecurity, string>> = {
  open: "Open",
  wpa2: "WPA2 Personal",
  "wpa2-enterprise": "WPA2-Enterprise",
};

/** How long the dialog keeps following "still applying" APs (backend SSID_PUSH_SETTLE_SECONDS). */
const PUSH_SETTLE_MS = 180_000;
const PENDING_OUTCOMES = new Set<SsidPushOutcome>(["pending", "unresolved"]);

/**
 * Fold a pass's per-device rows into what the dialog already knows, keyed by serial. This is
 * what makes a retry report BOTH passes: on 2026-09-14 the popup showed only the retry
 * ("Renamed on 0 APs. 1 failed.") and hid that five APs had already been renamed.
 */
function mergeRows(prev: Record<string, SsidPushRow>, rows: SsidPushRow[] | undefined) {
  const next = { ...prev };
  for (const r of rows ?? []) {
    if (r.outcome === "skipped" || r.outcome === "not_applicable") continue;
    next[r.serial] = r;
  }
  return next;
}

/** The device page for `serial` in the current scope (site when one is selected). */
function useDeviceHref(): (serial: string) => string {
  const orgId = useSession((s) => s.currentOrgId);
  const siteId = useSession((s) => s.currentSiteId);
  const base = siteId ? `/o/${orgId}/s/${siteId}` : `/o/${orgId}`;
  return (serial: string) => `${base}/devices/${encodeURIComponent(serial)}`;
}

/**
 * APs that site SSID operations SKIP (services/config_compat): stock firmware that refuses every
 * Meridian config, or an operator's "Exclude from Meridian config". Neutral on purpose -- they
 * are not failures and do not keep a rename partial (Jedi Home a47d78ca5d1e, 2026-10-11) -- with
 * a link to the device, where the reason and the Include again action live.
 */
function ExcludedDevicesNote({ devices }: { devices: SsidExcludedDevice[] }) {
  const href = useDeviceHref();
  if (devices.length === 0) return null;
  return (
    <div className="rounded border border-line bg-surface-2 px-3 py-2 text-xs text-ink-muted">
      <p className="font-medium text-ink">
        Not managed by site SSIDs ({devices.length} AP{devices.length === 1 ? "" : "s"})
      </p>
      <ul className="mt-1 flex flex-col gap-1">
        {devices.map((d) => (
          <li key={d.serial} className="flex items-start gap-2">
            <span
              className="shrink-0 rounded bg-surface px-1.5 py-0.5 text-[0.6875rem] font-semibold text-ink-muted"
              title={d.reason ?? undefined}
            >
              {d.label ?? "Not managed"}
            </span>
            <span className="min-w-0">
              <Link to={href(d.serial)} className="font-medium text-brand hover:underline">
                {d.name}
              </Link>
              {d.reason && <span className="ml-1 text-ink-subtle">— {d.reason}</span>}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function listNames(names: string[]): string {
  if (names.length <= 3) return names.join(", ");
  return `${names.slice(0, 2).join(", ")} and ${names.length - 2} more APs`;
}

/**
 * Per-AP outcome of a fan-out (rename or save): a one-line summary across every pass, then a
 * row per AP with its own verdict and reason. "Still applying" is its own state, never folded
 * into failure -- the AP that looked failed on 2026-09-14 had in fact taken the change.
 */
function PushOutcomePanel({
  verb,
  rows,
  following,
  followedOut,
  notSaved,
  footer,
}: {
  verb: string;
  rows: SsidPushRow[];
  following: boolean;
  followedOut: boolean;
  notSaved: string[];
  footer: string | null;
}) {
  const s = summarizeRows(rows);
  const clean = s.pending === 0 && s.failed === 0 && s.not_reached === 0;
  const pendingNames = rows.filter((r) => PENDING_OUTCOMES.has(r.outcome)).map((r) => r.name);
  // APs running a configuration the controller never sent. Counted as failed, but said on their
  // own line: the remedy is saving again (which re-sends it), not reading an error.
  const divergedNames = namesWith(rows, "diverged");
  // APs that REFUSED the configuration (uCentral error 1 with no `rejected` list, or >= 2).
  // Counted as failed and said on their own line: saving again sends the same refused document,
  // so the remedy is to change what is sent (or the AP's firmware), not to retry.
  const rejectedNames = namesWith(rows, "rejected");
  // Applied, minus parts the AP does not support. Counted as applied, never "clean".
  const warnedNames = namesWith(rows, "applied_with_warnings");
  // Config-incompatible APs NOT sent the change (services/config_compat). Neutral and apart:
  // they are not in "N of M" and never make the panel red -- the operation completed without
  // them, and saving again does not resend to them.
  const excludedNames = namesWith(rows, "excluded");
  const href = useDeviceHref();
  const hardFailed = s.failed - divergedNames.length - rejectedNames.length;
  const calm = clean && warnedNames.length === 0;
  const sorted = [...rows].sort(
    (a, b) => OUTCOME_ORDER[a.outcome] - OUTCOME_ORDER[b.outcome] || a.name.localeCompare(b.name),
  );
  return (
    <div
      role="status"
      aria-live="polite"
      className={`rounded border px-3 py-2 text-xs ${
        calm
          ? "border-healthy/40 bg-healthy-soft"
          : rejectedNames.length > 0 || hardFailed > 0
            ? "border-critical/40 bg-critical-soft"
            : "border-warn/40 bg-warn-soft"
      }`}
    >
      <p className="font-medium text-ink">
        {verb} on {s.applied} of {s.total} AP{s.total === 1 ? "" : "s"}
        {s.already > 0 && s.applied_now > 0 ? ` (${s.already} earlier, ${s.applied_now} now)` : ""}.
      </p>
      {pendingNames.length > 0 && (
        <p className="mt-0.5 text-warn">
          Still applying on {listNames(pendingNames)}
          {following
            ? " — checking again every few seconds."
            : followedOut
              ? " — not confirmed after 3 minutes. Check the AP; saving again is safe."
              : "."}
        </p>
      )}
      {rejectedNames.length > 0 && (
        <p className="mt-0.5 font-medium text-critical">
          Rejected by {listNames(rejectedNames)} — {rejectedNames.length === 1 ? "it is" : "they are"}{" "}
          still running the previous configuration. Saving again sends the same change; fix what
          the device refused first.
        </p>
      )}
      {hardFailed > 0 && <p className="mt-0.5 text-critical">{hardFailed} failed — reasons below.</p>}
      {warnedNames.length > 0 && (
        <p className="mt-0.5 text-warn">
          Applied on {listNames(warnedNames)} with parts dropped that the AP does not support —
          details below.
        </p>
      )}
      {divergedNames.length > 0 && (
        <p className="mt-0.5 text-critical">
          {listNames(divergedNames)} came back running an earlier configuration (likely after a
          reboot) — save again to re-send it.
        </p>
      )}
      {s.not_reached > 0 && <p className="mt-0.5 text-ink-muted">{s.not_reached} not reached — unchanged.</p>}
      {excludedNames.length > 0 && (
        <p className="mt-0.5 text-ink-muted">
          Not managed: {listNames(excludedNames)} — firmware can't apply Meridian config (reflash
          needed), so {excludedNames.length === 1 ? "it was" : "they were"} skipped, not failed.{" "}
          {excludedNames.length === 1 ? "It gets" : "They get"} the current SSIDs once{" "}
          {excludedNames.length === 1 ? "it" : "they"} can apply them.
        </p>
      )}
      <ul className="mt-2 max-h-48 divide-y divide-line overflow-y-auto rounded border border-line bg-surface">
        {sorted.map((r) => (
          <li key={r.serial} className="flex items-start justify-between gap-3 px-2 py-1.5">
            <div className="min-w-0">
              <div className="truncate text-ink">
                {r.outcome === "excluded" ? (
                  // Link to the device: its page carries the reason and "Include again".
                  <Link to={href(r.serial)} className="text-brand hover:underline">
                    {r.name}
                  </Link>
                ) : (
                  r.name
                )}
                {r.name !== r.serial && (
                  <span className="ml-1.5 font-mono text-[0.6875rem] text-ink-subtle">{r.serial}</span>
                )}
              </div>
              {r.reason && <div className={reasonTone(r.outcome)}>{r.reason}</div>}
            </div>
            <span
              className={`shrink-0 rounded px-1.5 py-0.5 text-[0.6875rem] font-semibold ${outcomeTone(r.outcome)}`}
            >
              {OUTCOME_LABEL[r.outcome]}
            </span>
          </li>
        ))}
      </ul>
      {notSaved.length > 0 && (
        <p className="mt-2 text-ink">
          <span className="font-semibold">Not saved yet:</span> {notSaved.join(", ")}. These are
          saved only after the rename has reached every AP — save again once it has.
        </p>
      )}
      {footer && <p className="mt-1 text-ink-muted">{footer}</p>}
    </div>
  );
}
// Schedule day tokens, Monday-first to match the backend (services/ssid_schedule.py).
const DAYS = [
  { key: "mon", label: "Mon" },
  { key: "tue", label: "Tue" },
  { key: "wed", label: "Wed" },
  { key: "thu", label: "Thu" },
  { key: "fri", label: "Fri" },
  { key: "sat", label: "Sat" },
  { key: "sun", label: "Sun" },
] as const;
const DEFAULT_WINDOW: SsidScheduleWindow = {
  days: ["mon", "tue", "wed", "thu", "fri"],
  start: "08:00",
  end: "18:00",
};

/** Broadcast-schedule editor: a master toggle + a list of day/time windows.
 *  Off => the SSID is always on (no schedule sent). Matches the dialog's field style. */
function ScheduleEditor({
  enabled,
  setEnabled,
  windows,
  setWindows,
}: {
  enabled: boolean;
  setEnabled: (v: boolean) => void;
  windows: SsidScheduleWindow[];
  setWindows: (w: SsidScheduleWindow[]) => void;
}) {
  const patch = (i: number, next: Partial<SsidScheduleWindow>) =>
    setWindows(windows.map((w, idx) => (idx === i ? { ...w, ...next } : w)));
  const toggleDay = (i: number, day: string) => {
    const w = windows[i];
    if (!w) return;
    const has = w.days.includes(day);
    patch(i, { days: has ? w.days.filter((d) => d !== day) : [...w.days, day] });
  };
  return (
    <div className="mt-1 border-t border-line pt-3">
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        <span className="text-[0.6875rem] font-semibold uppercase tracking-wide text-ink-subtle">
          Broadcast schedule
        </span>
      </label>
      {!enabled ? (
        <p className="mt-1 text-xs text-ink-muted">Always on. Enable to broadcast only during set hours.</p>
      ) : (
        <div className="mt-2 flex flex-col gap-3">
          {windows.map((w, i) => (
            <div key={i} className="rounded border border-line bg-surface-2 p-3">
              <div className="flex flex-wrap gap-1.5">
                {DAYS.map((d) => {
                  const on = w.days.includes(d.key);
                  return (
                    <button
                      key={d.key}
                      type="button"
                      onClick={() => toggleDay(i, d.key)}
                      className={`rounded px-2 py-1 text-xs font-medium ${
                        on
                          ? "bg-brand text-white"
                          : "border border-line bg-surface text-ink-muted hover:border-brand"
                      }`}
                    >
                      {d.label}
                    </button>
                  );
                })}
              </div>
              <div className="mt-2 flex items-center gap-2 text-sm">
                <input
                  type="time"
                  value={w.start}
                  onChange={(e) => patch(i, { start: e.target.value })}
                  className="rounded border border-line bg-surface px-2 py-1 font-mono text-ink outline-none focus:border-brand"
                />
                <span className="text-ink-muted">to</span>
                <input
                  type="time"
                  value={w.end}
                  onChange={(e) => patch(i, { end: e.target.value })}
                  className="rounded border border-line bg-surface px-2 py-1 font-mono text-ink outline-none focus:border-brand"
                />
                {w.start > w.end && (
                  <span className="text-xs text-ink-subtle" title="Crosses midnight">
                    overnight
                  </span>
                )}
                {windows.length > 1 && (
                  <button
                    type="button"
                    onClick={() => setWindows(windows.filter((_, idx) => idx !== i))}
                    className="ml-auto text-xs text-ink-subtle hover:text-critical"
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={() => setWindows([...windows, { ...DEFAULT_WINDOW }])}
            className="self-start text-xs font-medium text-brand hover:underline"
          >
            + Add window
          </button>
        </div>
      )}
    </div>
  );
}

export default function SSIDs() {
  const siteId = useSession((s) => s.currentSiteId);
  const siteName = useSession((s) => s.currentSite()?.name);
  const { data, isLoading, error } = useSsids(siteId);
  const [editing, setEditing] = useState<Ssid | null>(null);
  const [creating, setCreating] = useState(false);

  if (!siteId) {
    return (
      <div>
        <PageHeader title="SSIDs" subtitle="Wi-Fi networks" />
        <EmptyState
          title="Select a site first"
          hint="SSIDs apply to every AP in a site. Pick a site from the scope bar above."
        />
      </div>
    );
  }

  const ssids = data?.ssids ?? [];

  return (
    <div>
      <div className="flex items-start justify-between gap-3">
        <PageHeader title="SSIDs" subtitle={siteName ? `Wi-Fi networks in ${siteName}` : undefined} />
        <RoleGate min="site_operator" mode="hide">
          <Button variant="primary" onClick={() => setCreating(true)}>
            Add SSID
          </Button>
        </RoleGate>
      </div>

      {error && <ErrorBanner error={error} />}
      {isLoading && <div className="text-sm text-ink-muted">Loading SSIDs…</div>}

      {data && data.owgw_available === false && (
        <EmptyState title="No manageable APs" hint="No AP in this site has a live configuration to hold SSIDs." />
      )}

      {data && data.owgw_available && ssids.length === 0 && (
        <EmptyState title="No SSIDs yet" hint="Add a Wi-Fi network to broadcast it from every AP in this site." />
      )}

      {(data?.excluded_devices?.length ?? 0) > 0 && (
        <div className="mb-3">
          <ExcludedDevicesNote devices={data?.excluded_devices ?? []} />
        </div>
      )}

      {ssids.length > 0 && (
        <ul className="flex flex-col gap-2">
          {ssids.map((s) => (
            <SsidRow key={s.name} siteId={siteId} ssid={s} onEdit={() => setEditing(s)} />
          ))}
        </ul>
      )}

      {(creating || editing) && (
        <SsidDialog
          siteId={siteId}
          existing={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function SsidRow({ siteId, ssid, onEdit }: { siteId: string; ssid: Ssid; onEdit: () => void }) {
  const del = useDeleteSsid(siteId);
  const toggle = useToggleSsid(siteId);
  return (
    <li className={`md-card flex items-center gap-3 p-3 ${ssid.enabled ? "" : "opacity-60"}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate font-medium text-ink">{ssid.name}</span>
          {!ssid.enabled && (
            <span className="rounded bg-surface-2 px-1.5 py-0.5 text-[0.625rem] font-medium uppercase tracking-wide text-ink-subtle">
              Disabled
            </span>
          )}
          {ssid.hidden && (
            <span className="rounded border border-line px-1.5 py-0.5 text-[0.625rem] uppercase tracking-wide text-ink-subtle">
              Hidden
            </span>
          )}
          {ssid.schedule?.enabled && (
            <span
              className="rounded border border-line px-1.5 py-0.5 text-[0.625rem] uppercase tracking-wide text-ink-subtle"
              title="Broadcasts only during set hours"
            >
              Scheduled
            </span>
          )}
        </div>
        <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-ink-muted">
          <span>{SECURITY_LABEL[ssid.security]}</span>
          {ssid.radius_host && <span className="font-mono">RADIUS {ssid.radius_host}</span>}
          <span className="font-mono">{ssid.bands.join(" · ") || "—"}</span>
          <span className="font-mono">{ssid.vlan != null ? `VLAN ${ssid.vlan}` : "untagged"}</span>
          {/* OP#1778: say "All APs in site" out loud -- no target row means EVERY AP. */}
          <span>{targetLabel(ssid)}</span>
          {ssid.isolate && <span>isolated</span>}
          {(ssid.rate_down != null || ssid.rate_up != null) && (
            <span>
              {ssid.rate_down ?? "∞"}↓/{ssid.rate_up ?? "∞"}↑ Mbps
            </span>
          )}
          <span>
            {ssid.on_devices.length} AP{ssid.on_devices.length === 1 ? "" : "s"}
          </span>
          {/* An "All APs" SSID that is NOT on every AP: its next save spreads it to the rest
              (the save warns and asks first). Shown so the drift is visible before that. */}
          {coverageGap(ssid) && ssid.coverage && (
              <span
                className="text-warn"
                title="Broadcast on is 'All APs in site', but this SSID is only on some APs. Saving it will add it to the others."
              >
                on {ssid.coverage.on} of {ssid.coverage.of} APs
              </span>
            )}
        </div>
      </div>
      {/* OP#1818: trace the SSID's VLAN to the gateway. Read-only, so every role may. */}
      {ssid.vlan != null && <VlanPathButton siteId={siteId} ssid={ssid.name} variant="ghost" />}
      <RoleGate min="site_operator" mode="hide">
        <div className="flex shrink-0 items-center gap-2">
          <Button
            variant="ghost"
            disabled={toggle.isPending}
            onClick={() => toggle.mutate({ name: ssid.name, enable: !ssid.enabled })}
            title={ssid.enabled ? "Stop broadcasting (keeps the definition)" : "Broadcast this SSID again"}
          >
            {toggle.isPending ? "…" : ssid.enabled ? "Disable" : "Enable"}
          </Button>
          <Button variant="secondary" onClick={onEdit}>
            Edit
          </Button>
          <Button
            variant="ghost"
            disabled={del.isPending}
            onClick={() => del.mutate(ssid.name)}
            title="Remove this SSID from every AP in the site"
          >
            {del.isPending ? "Removing…" : "Remove"}
          </Button>
        </div>
      </RoleGate>
    </li>
  );
}

/**
 * The per-AP consequence of a save (OP#1778): which APs gain the SSID, which lose it, and
 * which are left broadcasting nothing. Names come from the backend preview (validate / 409).
 */
function ImpactPreview({ impact }: { impact: SsidImpact }) {
  const list = (aps: SsidImpactAp[]) => apListLabel(aps);
  const also = impact.also_repushed ?? [];
  const blocked = impact.repush_blocked ?? [];
  if (!impact.added.length && !impact.removed.length && !also.length && !blocked.length) return null;
  return (
    <dl className="mt-2 grid grid-cols-[auto,1fr] gap-x-2 gap-y-1">
      <dt className="text-ink-muted">Broadcast on</dt>
      <dd>{impact.target_label}</dd>
      {impact.added.length > 0 && (
        <>
          <dt className="text-ink-muted">Added to</dt>
          <dd>
            {impact.added.length} AP{impact.added.length === 1 ? "" : "s"}: {list(impact.added)}
          </dd>
        </>
      )}
      {impact.removed.length > 0 && (
        <>
          <dt className="text-ink-muted">Removed from</dt>
          <dd>
            {impact.removed.length} AP{impact.removed.length === 1 ? "" : "s"}: {list(impact.removed)}
          </dd>
        </>
      )}
      {impact.left_without_ssids.length > 0 && (
        <>
          <dt className="font-medium text-critical">No Wi-Fi left</dt>
          <dd className="font-medium text-critical">{list(impact.left_without_ssids)}</dd>
        </>
      )}
      {/* OP#1814: drift repair re-sends the held config to APs running a configuration the
          controller did not send -- a push to an AP the operator did not pick. Shown, never hidden. */}
      {also.length > 0 && (
        <>
          <dt className="text-ink-muted">Also re-sent to</dt>
          <dd title={also.map((r) => `${r.name}: ${r.reason}`).join("; ")}>
            {also.length} AP{also.length === 1 ? "" : "s"}: {list(also)} (running a configuration the
            controller did not send)
          </dd>
        </>
      )}
      {blocked.length > 0 && (
        <>
          <dt className="text-ink-muted">Not re-sent</dt>
          <dd title={blocked.map((r) => `${r.name}: ${r.reason}`).join("; ")}>
            {list(blocked)} (a push is still waiting for confirmation, or the gateway holds an older
            configuration)
          </dd>
        </>
      )}
    </dl>
  );
}

function SsidDialog({
  siteId,
  existing,
  onClose,
}: {
  siteId: string;
  existing: Ssid | null;
  onClose: () => void;
}) {
  const upsert = useUpsertSsid(siteId);
  const rename = useRenameSsid(siteId);
  const siteTags = useSiteTags(siteId);
  // Same query the page already holds (cached): which APs this save will NOT reach.
  const excludedDevices = useSsids(siteId).data?.excluded_devices ?? [];
  const [target, setTarget] = useState(existing?.target ?? "all");
  const [name, setName] = useState(existing?.name ?? "");
  const [security, setSecurity] = useState<SsidSecurity>(existing?.security ?? "wpa2");
  const [bands, setBands] = useState<string[]>(existing?.bands?.length ? existing.bands : ["2G", "5G"]);
  const [passphrase, setPassphrase] = useState("");
  const [hidden, setHidden] = useState(existing?.hidden ?? false);
  // PMF / 802.11w. "" = let the backend default by security (WPA3 required, else optional).
  const [pmf, setPmf] = useState(existing?.pmf ?? "");
  // Per-SSID client cap. "" = unlimited.
  const [maxClients, setMaxClients] = useState(existing?.max_clients != null ? String(existing.max_clients) : "");
  // Advanced options.
  const [vlan, setVlan] = useState(existing?.vlan != null ? String(existing.vlan) : "");
  const [isolate, setIsolate] = useState(existing?.isolate ?? false);
  // Multicast-to-unicast (airtime fix) defaults ON.
  const [unicastConversion, setUnicastConversion] = useState(existing?.unicast_conversion ?? true);
  const [rateDown, setRateDown] = useState(existing?.rate_down != null ? String(existing.rate_down) : "");
  const [rateUp, setRateUp] = useState(existing?.rate_up != null ? String(existing.rate_up) : "");
  // Broadcast schedule (timed enable/disable). Off = always on.
  const [schedEnabled, setSchedEnabled] = useState<boolean>(existing?.schedule?.enabled ?? false);
  const [windows, setWindows] = useState<SsidScheduleWindow[]>(
    existing?.schedule?.windows?.length ? existing.schedule.windows : [{ ...DEFAULT_WINDOW }],
  );
  // RADIUS (enterprise).
  const [radiusHost, setRadiusHost] = useState(existing?.radius_host ?? "");
  const [radiusPort, setRadiusPort] = useState(existing?.radius_port != null ? String(existing.radius_port) : "1812");
  const [radiusSecret, setRadiusSecret] = useState("");
  const [acctHost, setAcctHost] = useState(existing?.radius_acct_host ?? "");
  const [acctPort, setAcctPort] = useState(existing?.radius_acct_port != null ? String(existing.radius_acct_port) : "1813");
  const [acctSecret, setAcctSecret] = useState("");

  const isEdit = !!existing;
  // The name field is locked on edit until the operator deliberately unlocks it. Renaming is
  // a real operation (its own fan-out, and every client drops) rather than just another
  // editable attribute, so it is opt-in -- but it IS available, and the button says so.
  const [renaming, setRenaming] = useState(false);
  const nameChanged = isEdit && renaming && !!name.trim() && name.trim() !== existing!.name;

  const isEnterprise = security === "wpa2-enterprise" || security === "wpa3-enterprise";
  const secured = security !== "open" && !isEnterprise;
  // BYO RADIUS: "" = enter manually, otherwise a registered server's id.
  const [serverId, setServerId] = useState<string>("");
  const { data: rsData } = useRadiusServers();
  const radiusServers = rsData?.radius_servers ?? [];
  const usingServer = isEnterprise && serverId !== "";
  // RADIUS "secret follows the host" (D-18 follow-up; backend services/radius_destination).
  // A stored secret may only keep going to its own host: moving it needs a customer admin
  // (org_admin+) AND a re-entered secret. A site manager's host fields are locked while a
  // secret is stored (they can still pick a registered server); an admin who changes a host
  // must type the secret again. The server enforces the same rule (403 / 400).
  const canMoveRadius = useSession((s) => s.hasRole("org_admin"));
  const authHostLocked = isEdit && !!existing?.has_radius_secret && !canMoveRadius;
  const acctHostLocked = isEdit && !!existing?.has_radius_acct_secret && !canMoveRadius;
  const authMoved = isEdit && hostMoved(existing?.radius_host, radiusHost, existing?.has_radius_secret);
  const acctMoved =
    isEdit && hostMoved(existing?.radius_acct_host, acctHost, existing?.has_radius_acct_secret);
  // On edit, an empty passphrase/secret keeps the existing one (optional when already set) --
  // unless the host it would go to changed.
  const passphraseRequired = secured && !(isEdit && existing?.has_passphrase);
  const secretRequired =
    isEnterprise && !usingServer && (!(isEdit && existing?.has_radius_secret) || authMoved);
  const acctSecretRequired = isEnterprise && !usingServer && acctMoved;

  /**
   * WHICH fields besides the name did the operator edit?
   *
   * This gates whether a rename is followed by a full upsert, so it must err on the side of
   * TRUE: a false negative silently discards a real edit, while a false positive only costs
   * a redundant (idempotent) push. Every field the form can send is compared, and any
   * write-only secret being present at all counts as a change — we cannot compare those
   * against the server, which never returns them. The LABELS are what the dialog names when a
   * partial rename stops the flow before these fields are saved.
   */
  const changedFields: string[] = [];
  if (isEdit) {
    const was = existing!;
    const mark = (changed: boolean, label: string) => {
      if (changed && !changedFields.includes(label)) changedFields.push(label);
    };
    mark(security !== (was.security ?? "wpa2"), "Security");
    mark(bands.join() !== (was.bands?.length ? was.bands : ["2G", "5G"]).join(), "Bands");
    mark(hidden !== (was.hidden ?? false), "Hidden network");
    mark(isolate !== (was.isolate ?? false), "Client isolation");
    mark(unicastConversion !== (was.unicast_conversion ?? true), "Multicast-to-unicast");
    mark(pmf !== (was.pmf ?? ""), "Protected Management Frames");
    mark(maxClients !== (was.max_clients != null ? String(was.max_clients) : ""), "Max clients");
    mark(vlan !== (was.vlan != null ? String(was.vlan) : ""), "VLAN");
    mark(rateDown !== (was.rate_down != null ? String(was.rate_down) : ""), "Download cap");
    mark(rateUp !== (was.rate_up != null ? String(was.rate_up) : ""), "Upload cap");
    mark(target !== (was.target ?? "all"), "Broadcast on");
    mark(
      schedEnabled !== (was.schedule?.enabled ?? false) ||
        JSON.stringify(windows) !==
          JSON.stringify(was.schedule?.windows?.length ? was.schedule.windows : [DEFAULT_WINDOW]),
      "Schedule",
    );
    mark(
      radiusHost !== (was.radius_host ?? "") ||
        radiusPort !== (was.radius_port != null ? String(was.radius_port) : "1812") ||
        serverId !== "",
      "RADIUS server",
    );
    mark(
      acctHost !== (was.radius_acct_host ?? "") ||
        acctPort !== (was.radius_acct_port != null ? String(was.radius_acct_port) : "1813"),
      "RADIUS accounting",
    );
    // Write-only values: the server never returns them, so any entry is an edit.
    mark(!!passphrase, "Passphrase");
    mark(!!radiusSecret, "RADIUS secret");
    mark(!!acctSecret, "Accounting secret");
  }
  const otherFieldsChanged = !isEdit || changedFields.length > 0;

  // 6 GHz requires WPA3/OWE. Mirrored here so the operator sees it before saving; the backend
  // refuses the same combination (services/ssid_manager._validate).
  const sixGhzBlocked = bands.includes("6G") && security in NOT_ALLOWED_ON_6G;

  const validate = useValidateSsid(siteId);
  // Backend refusals / acknowledge-before-save warnings from the dry run or a 409.
  const [serverErrors, setServerErrors] = useState<string[]>([]);
  const [warnings, setWarnings] = useState<SsidWarning[]>([]);
  // OP#1778: what the save does to each AP (added / removed / left with no SSID), from the
  // dry run or the 409, and the explicit tick a HARD warning (an AP left dark) needs.
  const [impact, setImpact] = useState<SsidImpact | null>(null);
  const [ackNoSsid, setAckNoSsid] = useState(false);
  // OP#1818: the VLAN path from the targeted APs (compact), from the dry run.
  const [pathCheck, setPathCheck] = useState<VlanPathCompact | null>(null);
  // INFO notes from the dry run (owner decision 2026-10-14): shown, never acknowledged.
  const [notes, setNotes] = useState<SsidWarning[]>([]);
  const hardWarnings = pickHard(warnings);
  const darkCount = darkAps(warnings).length;
  // Double-submit guard (OP#1778 point 2, the 19:07:29Z double POST): a ref, not state, so a
  // second click in the same frame -- before React re-renders the disabled button -- is dropped.
  const inFlight = useRef(false);
  // The fields a stopped rename left unsaved, snapshotted when the flow stopped.
  const [notSaved, setNotSaved] = useState<string[]>([]);
  // Per-AP outcomes, merged across every pass made from this dialog.
  const [renameRows, setRenameRows] = useState<Record<string, SsidPushRow>>({});
  const [saveRows, setSaveRows] = useState<Record<string, SsidPushRow>>({});
  // Follow-up polling for "still applying" APs: until this epoch-ms, then give up and say so.
  const [followUntil, setFollowUntil] = useState(0);
  const [followedOut, setFollowedOut] = useState(false);

  const pendingSerials = [...Object.values(renameRows), ...Object.values(saveRows)]
    .filter((r) => PENDING_OUTCOMES.has(r.outcome))
    .map((r) => r.serial);
  const following = pendingSerials.length > 0 && followUntil > 0;
  const pushStatus = useSsidPushStatus(siteId, pendingSerials, following);

  // Fold each status poll into the rows that are still pending (never overwrite a settled row).
  useEffect(() => {
    const polled = pushStatus.data?.results;
    if (!polled?.length) return;
    const fold = (prev: Record<string, SsidPushRow>) => {
      let next = prev;
      for (const r of polled) {
        const cur = prev[r.serial];
        if (!cur || !PENDING_OUTCOMES.has(cur.outcome)) continue;
        if (next === prev) next = { ...prev };
        next[r.serial] = r;
      }
      return next;
    };
    setRenameRows(fold);
    setSaveRows(fold);
  }, [pushStatus.data]);

  // Stop following at the settle horizon, and remember that we stopped (the panel says so).
  useEffect(() => {
    if (!followUntil) return;
    const t = setTimeout(() => {
      setFollowUntil(0);
      setFollowedOut(true);
    }, Math.max(followUntil - Date.now(), 0));
    return () => clearTimeout(t);
  }, [followUntil]);

  const startFollowing = () => {
    setFollowedOut(false);
    setFollowUntil(Date.now() + PUSH_SETTLE_MS);
  };

  const toggleBand = (b: string) =>
    setBands((prev) => (prev.includes(b) ? prev.filter((x) => x !== b) : [...prev, b]));

  /** The upsert body. `acknowledge` re-sends after the operator has read the warnings. */
  const buildBody = (acknowledge: boolean) => ({
    name: name.trim(),
    security,
    bands,
    hidden,
    isolate,
    ...(pmf ? { pmf } : {}),
    max_clients: maxClients.trim() === "" ? null : Number(maxClients),
    unicast_conversion: unicastConversion,
    // Off => an explicit disabled schedule, which the backend normalizes to "always on".
    schedule: schedEnabled ? { enabled: true, windows } : { enabled: false, windows: [] },
    target,
    vlan: vlan.trim() === "" ? null : Number(vlan),
    rate_down: rateDown.trim() === "" ? null : Number(rateDown),
    rate_up: rateUp.trim() === "" ? null : Number(rateUp),
    ...(passphrase ? { passphrase } : {}),
    ...(isEnterprise
      ? usingServer
        ? { radius_server_id: Number(serverId) }
        : {
            radius_host: radiusHost.trim(),
            radius_port: radiusPort.trim() ? Number(radiusPort) : 1812,
            ...(radiusSecret ? { radius_secret: radiusSecret } : {}),
            ...(acctHost.trim()
              ? {
                  radius_acct_host: acctHost.trim(),
                  radius_acct_port: acctPort.trim() ? Number(acctPort) : 1813,
                  ...(acctSecret ? { radius_acct_secret: acctSecret } : {}),
                }
              : {}),
          }
      : {}),
    ...(acknowledge ? { acknowledge_warnings: true } : {}),
    ...(acknowledge && ackNoSsid ? { acknowledge_no_ssid_aps: true } : {}),
  });

  /** One save at a time from this dialog, whatever the button state says (see inFlight). */
  const runSave = async (acknowledge: boolean) => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      await runSaveOnce(acknowledge);
    } finally {
      inFlight.current = false;
    }
  };

  const runSaveOnce = async (acknowledge: boolean) => {
    if (!name.trim() || bands.length === 0 || sixGhzBlocked) return;
    if (passphraseRequired && passphrase.length < 8) return;
    if (isEnterprise && !usingServer && !radiusHost.trim()) return;
    if (secretRequired && !radiusSecret) return;
    if (acctSecretRequired && !acctSecret) return;
    setServerErrors([]);
    setWarnings([]);
    setNotSaved([]);
    if (!acknowledge) {
      setImpact(null);
      setAckNoSsid(false);
    }
    const body = buildBody(acknowledge);

    // Dry run FIRST whenever more than the name is being saved. A rename lands before the
    // other fields, so a refusal or warning discovered afterwards would leave the operator
    // half-saved. A dry-run transport failure falls through: the save itself still enforces.
    if (otherFieldsChanged && !acknowledge) {
      let check: SsidValidation | null = null;
      try {
        // rename_from: during a rename the APs still carry the OLD name, so the preview must
        // count them as carrying it (otherwise every AP reads as an "addition").
        check = await validate.mutateAsync(
          nameChanged ? { ...body, rename_from: existing!.name } : body,
        );
      } catch {
        check = null;
      }
      setImpact(check?.impact ?? null);
      setPathCheck(check?.path_check ?? null);
      setNotes(check?.notes ?? []);
      if (check?.errors.length) {
        setServerErrors(check.errors);
        return;
      }
      if (check?.warnings.length) {
        setWarnings(check.warnings);
        return;
      }
    }

    // A rename is a SEPARATE call and must land FIRST, so the upsert below addresses the
    // SSID by its new name and merges into it. Sending the new name straight to the upsert
    // would create a second network beside the original instead of renaming it.
    if (nameChanged) {
      let result;
      try {
        // The AWAITED result, not rename.data: the hook's cached value belongs to the render
        // closure this handler was created in.
        result = await rename.mutateAsync({ name: existing!.name, newName: name.trim() });
      } catch {
        return; // the reason is rendered below
      }
      setRenameRows((prev) => mergeRows(prev, result.results));
      // A rename that has not been CONFIRMED on every AP stops here, whatever else was edited
      // -- including "still applying": the upsert would run under the new name while an AP
      // may still broadcast the old one, and owgw would refuse it as busy anyway. The panel
      // names exactly which fields were not saved.
      if (result.partial || result.errors || result.unresolved || result.not_attempted || result.pending) {
        if (result.pending || result.unresolved) startFollowing();
        if (otherFieldsChanged) setNotSaved(changedFields);
        return;
      }
      // RENAME ONLY, and it was clean: done. Falling through would RECONCILE the SSID against
      // its target -- on 2026-09-11 that turned a one-AP rename into a site-wide deployment.
      if (!otherFieldsChanged) {
        onClose();
        return;
      }
    }

    let saved;
    try {
      saved = await upsert.mutateAsync(body);
    } catch (err) {
      // 409 = valid but needs acknowledgement (e.g. the dry run was skipped or raced), or
      // another save of this SSID is still running (code ssid_save_in_progress).
      if (err instanceof ApiError && err.status === 409) {
        const b = err.body as {
          warnings?: SsidWarning[];
          impact?: SsidImpact | null;
          error?: string;
        } | null;
        if (b?.impact) setImpact(b.impact);
        if (b?.warnings?.length) setWarnings(b.warnings);
        else if (b?.error) setServerErrors([b.error]);
      }
      return;
    }
    setSaveRows((prev) => mergeRows(prev, saved.results));
    // A partial save stays open with the per-AP outcome; saving again is idempotent.
    if (saved.pending || saved.errors || saved.unresolved || saved.not_attempted) {
      if (saved.pending || saved.unresolved) startFollowing();
      return;
    }
    onClose();
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    void runSave(false);
  };

  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-sheet bg-ink/40" />
        <Dialog.Content className="md-dialog fixed left-1/2 top-1/2 z-sheet w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-line bg-overlay p-5 shadow-sheet outline-none">
          <Dialog.Title className="text-lg font-semibold text-ink">
            {isEdit ? "Edit SSID" : "Add SSID"}
          </Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-ink-muted">
            {target === "all" ? "Applies to every AP in this site" : `Applies only to APs in group “${target}”`}
            {excludedDevices.length > 0 ? " that is under Meridian config." : "."} APs outside the
            target lose this SSID on save.
          </Dialog.Description>
          {excludedDevices.length > 0 && (
            <div className="mt-2">
              <ExcludedDevicesNote devices={excludedDevices} />
            </div>
          )}
          <form onSubmit={submit} className="mt-4 flex flex-col gap-3">
            <label className="flex flex-col gap-1 text-sm">
              <span className="flex items-center justify-between gap-2">
                <span className="text-ink-muted">Network name (SSID)</span>
                {isEdit && !renaming && (
                  <button
                    type="button"
                    onClick={() => setRenaming(true)}
                    className="text-xs text-brand underline-offset-2 hover:underline"
                  >
                    Rename
                  </button>
                )}
              </span>
              <input
                autoFocus={!isEdit}
                value={name}
                readOnly={isEdit && !renaming}
                onChange={(e) => setName(e.target.value)}
                placeholder="Guest-WiFi"
                className="rounded border border-line bg-surface px-3 py-2 text-ink outline-none focus:border-brand read-only:opacity-70"
              />
              {isEdit && !renaming && (
                <span className="text-xs text-ink-subtle">
                  Locked while editing — renaming disconnects clients, so it is a separate step.
                </span>
              )}
              {/* The consequence is stated where the action is taken, not in a tooltip: every
                  station is associated to the SSID by NAME, so a rename is a new network to
                  every device in the building. */}
              {nameChanged && (
                <span className="text-xs text-warn">
                  Renaming “{existing!.name}” to “{name.trim()}” disconnects every client on this
                  network. They must reconnect to the new name; saved Wi-Fi profiles will not
                  follow it.
                </span>
              )}
              {renaming && !nameChanged && (
                <span className="text-xs text-ink-subtle">
                  Enter a new name, or leave it unchanged to save the other settings only.
                </span>
              )}
            </label>

            <div className="flex flex-col gap-1 text-sm">
              <span className="text-ink-muted">Bands</span>
              <div className="flex gap-3">
                {ALL_BANDS.map((b) => (
                  <label key={b} className="flex items-center gap-1.5">
                    <input type="checkbox" checked={bands.includes(b)} onChange={() => toggleBand(b)} />
                    <span>{b}</span>
                  </label>
                ))}
              </div>
            </div>

            <label className="flex flex-col gap-1 text-sm">
              <span className="text-ink-muted">Broadcast on</span>
              <select
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                className="rounded border border-line bg-surface px-3 py-2 text-ink outline-none focus:border-brand"
              >
                <option value="all">All APs in the site</option>
                {(siteTags.data?.tags ?? []).map((t) => (
                  <option key={t.tag} value={t.tag}>
                    Group: {t.tag} ({t.count} AP{t.count === 1 ? "" : "s"})
                  </option>
                ))}
                {/* Keep a stale target selectable even if the tag has no APs yet. */}
                {target !== "all" && !(siteTags.data?.tags ?? []).some((t) => t.tag === target) && (
                  <option value={target}>Group: {target}</option>
                )}
              </select>
            </label>

            <label className="flex flex-col gap-1 text-sm">
              <span className="text-ink-muted">Security</span>
              <select
                value={security}
                onChange={(e) => setSecurity(e.target.value as SsidSecurity)}
                className="rounded border border-line bg-surface px-3 py-2 text-ink outline-none focus:border-brand"
              >
                <option value="open">Open (no password)</option>
                <option value="wpa2">WPA2 Personal</option>
                <option value="wpa3">WPA3 Personal</option>
                <option value="wpa2/3">WPA2/3 mixed</option>
                <option value="wpa2-enterprise">WPA2-Enterprise (RADIUS)</option>
                <option value="wpa3-enterprise">WPA3-Enterprise (RADIUS)</option>
              </select>
            </label>
            {sixGhzBlocked && (
              <span role="alert" className="-mt-1 text-xs text-critical">
                6 GHz cannot carry a {NOT_ALLOWED_ON_6G[security]} network. Wi-Fi 6E/7 requires
                WPA3 on 6 GHz (WPA3-SAE, WPA3-Enterprise, or OWE instead of Open), and the AP will
                not broadcast it on that band. Choose WPA3 Personal or WPA3-Enterprise, or untick
                6G.
              </span>
            )}

            {security !== "open" && (
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-ink-muted">
                  Protected Management Frames (802.11w)
                </span>
                <select
                  value={pmf}
                  onChange={(e) => setPmf(e.target.value)}
                  className="rounded border border-line bg-surface px-3 py-2 text-ink outline-none focus:border-brand"
                >
                  {/* "" lets the backend default by security: WPA3 required, else optional. */}
                  <option value="">Auto (by security)</option>
                  <option value="disabled">Disabled</option>
                  <option value="optional">Optional</option>
                  <option value="required">Required (WPA3)</option>
                </select>
              </label>
            )}

            {secured && (
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-ink-muted">
                  Passphrase{isEdit && existing?.has_passphrase ? " (leave blank to keep current)" : ""}
                </span>
                <input
                  type="password"
                  value={passphrase}
                  onChange={(e) => setPassphrase(e.target.value)}
                  placeholder="At least 8 characters"
                  className="rounded border border-line bg-surface px-3 py-2 font-mono text-ink outline-none focus:border-brand"
                />
              </label>
            )}

            {isEnterprise && (
              <div className="rounded border border-line bg-surface-2 p-3">
                <div className="mb-2 text-[0.6875rem] font-semibold uppercase tracking-wide text-ink-subtle">
                  RADIUS server
                </div>
                {radiusServers.length > 0 && (
                  <label className="mb-2 flex flex-col gap-1 text-sm">
                    <span className="text-ink-muted">Use a registered server</span>
                    <select
                      value={serverId}
                      onChange={(e) => setServerId(e.target.value)}
                      className="rounded border border-line bg-surface px-3 py-2 text-ink outline-none focus:border-brand"
                    >
                      <option value="">Enter manually…</option>
                      {radiusServers.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name} ({s.auth_host})
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                {!usingServer && (<>
                <div className="grid grid-cols-3 gap-2">
                  <label className="col-span-2 flex flex-col gap-1 text-sm">
                    <span className="text-ink-muted">Host</span>
                    <input
                      value={radiusHost}
                      readOnly={authHostLocked}
                      title={authHostLocked ? HOST_LOCKED_HINT : undefined}
                      onChange={(e) => setRadiusHost(e.target.value)}
                      placeholder="10.0.0.9"
                      className="rounded border border-line bg-surface px-3 py-2 font-mono text-ink outline-none focus:border-brand read-only:opacity-70"
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-sm">
                    <span className="text-ink-muted">Port</span>
                    <input
                      inputMode="numeric"
                      value={radiusPort}
                      onChange={(e) => setRadiusPort(e.target.value.replace(/[^0-9]/g, ""))}
                      placeholder="1812"
                      className="rounded border border-line bg-surface px-3 py-2 font-mono text-ink outline-none focus:border-brand"
                    />
                  </label>
                </div>
                {authHostLocked && (
                  <span className="mt-1 block text-xs text-ink-subtle">{HOST_LOCKED_HINT}</span>
                )}
                <label className="mt-2 flex flex-col gap-1 text-sm">
                  <span className="text-ink-muted">
                    Shared secret
                    {authMoved
                      ? ` (${REENTER_HINT})`
                      : isEdit && existing?.has_radius_secret
                        ? " (leave blank to keep current)"
                        : ""}
                  </span>
                  <input
                    type="password"
                    value={radiusSecret}
                    onChange={(e) => setRadiusSecret(e.target.value)}
                    placeholder="RADIUS shared secret"
                    className="rounded border border-line bg-surface px-3 py-2 font-mono text-ink outline-none focus:border-brand"
                  />
                </label>

                <div className="mt-3 border-t border-line pt-2 text-[0.6875rem] font-semibold uppercase tracking-wide text-ink-subtle">
                  Accounting (optional)
                </div>
                <div className="mt-1 grid grid-cols-3 gap-2">
                  <label className="col-span-2 flex flex-col gap-1 text-sm">
                    <span className="text-ink-muted">Host</span>
                    <input
                      value={acctHost}
                      readOnly={acctHostLocked}
                      title={acctHostLocked ? HOST_LOCKED_HINT : undefined}
                      onChange={(e) => setAcctHost(e.target.value)}
                      placeholder="none"
                      className="rounded border border-line bg-surface px-3 py-2 font-mono text-ink outline-none focus:border-brand read-only:opacity-70"
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-sm">
                    <span className="text-ink-muted">Port</span>
                    <input
                      inputMode="numeric"
                      value={acctPort}
                      onChange={(e) => setAcctPort(e.target.value.replace(/[^0-9]/g, ""))}
                      placeholder="1813"
                      className="rounded border border-line bg-surface px-3 py-2 font-mono text-ink outline-none focus:border-brand"
                    />
                  </label>
                </div>
                {acctHost.trim() && (
                  <label className="mt-2 flex flex-col gap-1 text-sm">
                    <span className="text-ink-muted">
                      Accounting secret
                      {acctMoved
                        ? ` (${REENTER_HINT})`
                        : isEdit && existing?.has_radius_acct_secret
                          ? " (leave blank to keep current)"
                          : ""}
                    </span>
                    <input
                      type="password"
                      value={acctSecret}
                      onChange={(e) => setAcctSecret(e.target.value)}
                      placeholder="shared secret"
                      className="rounded border border-line bg-surface px-3 py-2 font-mono text-ink outline-none focus:border-brand"
                    />
                  </label>
                )}
                </>)}
              </div>
            )}

            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={hidden} onChange={(e) => setHidden(e.target.checked)} />
              <span>Hidden network (don’t broadcast the name)</span>
            </label>

            {/* Advanced */}
            <div className="mt-1 border-t border-line pt-3">
              <div className="mb-2 text-[0.6875rem] font-semibold uppercase tracking-wide text-ink-subtle">
                Advanced
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-ink-muted">VLAN</span>
                  <input
                    inputMode="numeric"
                    value={vlan}
                    onChange={(e) => setVlan(e.target.value.replace(/[^0-9]/g, ""))}
                    placeholder="untagged"
                    className="rounded border border-line bg-surface px-3 py-2 font-mono text-ink outline-none focus:border-brand"
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-ink-muted">Max clients</span>
                  <input
                    inputMode="numeric"
                    value={maxClients}
                    onChange={(e) => setMaxClients(e.target.value.replace(/[^0-9]/g, ""))}
                    placeholder="unlimited"
                    className="rounded border border-line bg-surface px-3 py-2 font-mono text-ink outline-none focus:border-brand"
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-ink-muted">Download cap (Mbps)</span>
                  <input
                    inputMode="numeric"
                    value={rateDown}
                    onChange={(e) => setRateDown(e.target.value.replace(/[^0-9]/g, ""))}
                    placeholder="unlimited"
                    className="rounded border border-line bg-surface px-3 py-2 font-mono text-ink outline-none focus:border-brand"
                  />
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={isolate} onChange={(e) => setIsolate(e.target.checked)} />
                  <span>Client isolation</span>
                </label>
                <label className="flex items-center gap-2 text-sm" title="Convert multicast to unicast — clears Wi-Fi airtime for IPTV/casting. Recommended ON.">
                  <input type="checkbox" checked={unicastConversion} onChange={(e) => setUnicastConversion(e.target.checked)} />
                  <span>Multicast-to-unicast</span>
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-ink-muted">Upload cap (Mbps)</span>
                  <input
                    inputMode="numeric"
                    value={rateUp}
                    onChange={(e) => setRateUp(e.target.value.replace(/[^0-9]/g, ""))}
                    placeholder="unlimited"
                    className="rounded border border-line bg-surface px-3 py-2 font-mono text-ink outline-none focus:border-brand"
                  />
                </label>
              </div>
            </div>

            <ScheduleEditor
              enabled={schedEnabled}
              setEnabled={setSchedEnabled}
              windows={windows}
              setWindows={setWindows}
            />

            {/* The backend's own reason ("already exists in this site", "not found") is far
                more useful here than a generic failure, and it is the operator's own text.
                It is NOT followed by a reassurance: an earlier version appended "The network
                is unchanged", which is exactly wrong when the failure came after some APs had
                already been pushed — the case that actually happened on 2026-09-11. The
                backend now says what it did and did not do; pass that through and add
                nothing. */}
            {serverErrors.length > 0 && (
              <div role="alert" className="flex flex-col gap-1 text-xs text-critical">
                {serverErrors.map((m) => (
                  <p key={m}>{m}</p>
                ))}
              </div>
            )}
            {/* INFO notes (owner decision 2026-10-14): e.g. a VLAN path hop that cannot be
                checked (SonicFi until OP#1820). Never acknowledged; they never hold a save. */}
            {notes.length > 0 && (
              <div role="status" className="rounded border border-info/40 bg-info-soft px-3 py-2 text-xs text-ink">
                <p className="font-semibold text-info">Note</p>
                <ul className="mt-1 list-disc space-y-1 pl-4">
                  {notes.map((n) => (
                    <li key={n.code}>{n.message}</li>
                  ))}
                </ul>
                {warnings.length === 0 && <PathCompactLine pc={pathCheck} />}
              </div>
            )}
            {/* Acknowledge-before-save: the backend's own warning text (e.g. a VLAN no gateway
                serves). Nothing has been written or pushed while this is showing. */}
            {warnings.length > 0 && (
              <div
                role="alert"
                className={`rounded border px-3 py-2 text-xs text-ink ${
                  hardWarnings.length ? "border-critical/50 bg-surface-2" : "border-warn/40 bg-warn-soft"
                }`}
              >
                <p className={`font-semibold ${hardWarnings.length ? "text-critical" : "text-warn"}`}>
                  {hardWarnings.length ? "This save leaves APs with no Wi-Fi" : "Check before saving"}
                </p>
                <ul className="mt-1 list-disc space-y-1 pl-4">
                  {warnings.map((w) => (
                    <li
                      key={`${w.code}-${w.band ?? ""}`}
                      className={
                        w.severity === "hard" || w.code === "vlan_path_broken"
                          ? "font-medium text-critical"
                          : undefined
                      }
                    >
                      {w.message}
                    </li>
                  ))}
                </ul>
                {impact && <ImpactPreview impact={impact} />}
                {/* OP#1818: the path check behind a vlan_path_broken warning, with the full chain. */}
                {hasPathWarning(warnings) && (
                  <div className="mt-2 flex flex-col gap-1">
                    <PathCompactLine pc={pathCheck} />
                    {vlan.trim() !== "" && (
                      <div>
                        <VlanPathButton siteId={siteId} vlan={Number(vlan)} label="Show the path" variant="ghost" />
                      </div>
                    )}
                  </div>
                )}
                {hardWarnings.length > 0 && (
                  <label className="mt-2 flex items-start gap-2 font-medium text-critical">
                    <input
                      type="checkbox"
                      checked={ackNoSsid}
                      onChange={(e) => setAckNoSsid(e.target.checked)}
                    />
                    <span>
                      I understand {darkCount || "these"} AP{darkCount === 1 ? "" : "s"} will broadcast no Wi-Fi
                      at all after this save.
                    </span>
                  </label>
                )}
                <div className="mt-2 flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    type="button"
                    onClick={() => {
                      setWarnings([]);
                      setAckNoSsid(false);
                    }}
                  >
                    Go back
                  </Button>
                  <Button
                    variant="primary"
                    type="button"
                    disabled={
                      !canSaveAnyway(warnings, ackNoSsid) ||
                      upsert.isPending ||
                      rename.isPending ||
                      validate.isPending
                    }
                    onClick={() => void runSave(true)}
                  >
                    {upsert.isPending || rename.isPending ? "Saving…" : "Save anyway"}
                  </Button>
                </div>
              </div>
            )}
            {rename.isError && (
              <span className="text-xs text-critical">
                Could not rename: {(rename.error as Error)?.message ?? "the push failed"}
              </span>
            )}
            {/* A rename has its own fan-out and its own per-AP result, merged across every pass
                made from this dialog -- a retry must never read "Renamed on 0 APs" when the
                first pass renamed five. */}
            {Object.keys(renameRows).length > 0 && (
              <PushOutcomePanel
                verb="Renamed"
                rows={Object.values(renameRows)}
                following={following}
                followedOut={followedOut}
                notSaved={notSaved}
                footer={
                  rename.data?.partial
                    ? `Until every AP confirms, the controller still calls this network “${rename.data.name}”. ` +
                      "Click Rename and save again to finish — APs already renamed are skipped."
                    : null
                }
              />
            )}
            {upsert.isError && !(upsert.error instanceof ApiError && upsert.error.status === 409) && (
              <span className="text-xs text-critical">
                Could not save the SSID: {(upsert.error as Error)?.message ?? "the push failed"}
              </span>
            )}
            {Object.keys(saveRows).length > 0 && (
              <PushOutcomePanel
                verb="Saved"
                rows={Object.values(saveRows)}
                following={following}
                followedOut={followedOut}
                notSaved={[]}
                footer="Saving again is safe: APs already up to date are skipped."
              />
            )}

            {/* Sticky action row (md-dialog-footer): Save stays on-screen while the tall
                Edit SSID form scrolls inside the viewport-capped dialog (UI review 2026-10-17). */}
            <div className="md-dialog-footer flex items-center justify-end gap-2" data-testid="ssid-dialog-footer">
              <Button variant="ghost" type="button" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={
                  !name.trim() ||
                  bands.length === 0 ||
                  sixGhzBlocked ||
                  upsert.isPending ||
                  rename.isPending ||
                  validate.isPending
                }
              >
                {validate.isPending
                  ? "Checking…"
                  : rename.isPending
                  ? "Renaming…"
                  : upsert.isPending
                    ? "Saving…"
                    : nameChanged
                      ? "Rename and save"
                      : isEdit
                        ? "Save"
                        : "Create"}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
